import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TranslateIcon from "@mui/icons-material/Translate";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {Badge, Box, IconButton, ListItem, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React from "react";
import {useTranslation} from "react-i18next";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {getSurveyQuestionTypeIcon, getSurveyQuestionTypeLabelKey} from "../shared/surveyQuestionTypes.util";

export type QuestionRowData = {
    question: SurveyQuestion
    number?: number
    title: string
    missingLanguages: string[]
    visibilitySummaries: string[]
};

const HANDLE_SLOT_WIDTH = 30;

/*
    The pieces a question row shares with a section divider (SurveySectionDivider), so the two kinds of
    row line up and read alike.
*/

interface RowDragHandleProps {
    handleProps?: Record<string, unknown>
    lifted?: boolean
}

/** The grip a row is dragged by. A row that cannot move keeps the slot, so the titles still line up. */
export function RowDragHandle({handleProps, lifted}: RowDragHandleProps) {
    if (!handleProps) return <Box sx={{width: HANDLE_SLOT_WIDTH, flexShrink: 0}}/>;

    return (
        <IconButton
            size={'small'}
            {...handleProps}
            sx={{
                flexShrink: 0,
                color: 'text.secondary',
                cursor: lifted ? 'grabbing' : 'grab',
                touchAction: 'none',
            }}
            onClick={(event) => event.stopPropagation()}
        >
            <DragIndicatorIcon fontSize={'small'}/>
        </IconButton>
    );
}

interface RowMenuButtonProps {
    /** The row as staff read it ("3. How was breakfast?", "Section 2: Your room"), so each ⋮ has its own name. */
    name: string
    onOpenMenu: (anchor: HTMLElement) => void
}

export function RowMenuButton({name, onOpenMenu}: RowMenuButtonProps) {
    const {t} = useTranslation();

    return (
        <IconButton
            size={'small'}
            aria-label={t('More actions for {{name}}', {name})}
            sx={{flexShrink: 0}}
            onClick={(event) => {
                event.stopPropagation();
                onOpenMenu(event.currentTarget);
            }}
        >
            <MoreVertIcon fontSize={'small'}/>
        </IconButton>
    );
}

interface MissingLanguagesMarkerProps {
    languages: string[]
}

/** The survey languages a row's text is still missing in; nothing when there are none. */
export function MissingLanguagesMarker({languages}: MissingLanguagesMarkerProps) {
    const {t} = useTranslation();

    if (!languages.length) return null;

    return (
        <Tooltip
            title={t('Missing translation: {{languages}}', {
                languages: languages.map(code => code.toUpperCase()).join(', ')
            })}
        >
            <TranslateIcon sx={{fontSize: 16, color: 'warning.main', flexShrink: 0}}/>
        </Tooltip>
    );
}

interface BaseProps {
    row: QuestionRowData
    selected: boolean
    lifted?: boolean
    handleProps?: Record<string, unknown>
    onSelect: () => void
    onOpenMenu: (anchor: HTMLElement) => void
}

export function SurveyQuestionRowBase({
    row,
    selected,
    lifted,
    handleProps,
    onSelect,
    onOpenMenu
}: BaseProps) {
    const {t} = useTranslation();
    const {question} = row;

    const label = row.title || t(getSurveyQuestionTypeLabelKey(question.type));
    const name = row.number != null ? `${row.number}. ${label}` : label;

    // A switched-off question stays in the list, faded; its marker says why.
    const off = !!question.switchedOff;
    const faded = off ? {opacity: 0.5} : undefined;

    const visibilityIcon = <FilterAltOutlinedIcon sx={{fontSize: 16, color: 'info.main', flexShrink: 0}}/>;

    return (
        <ListItem
            disableGutters
            onClick={onSelect}
            sx={{
                minHeight: 44,
                px: 1,
                gap: 1,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: selected ? 'action.selected' : 'transparent',
                ...(lifted && {
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                }),
                '&:hover': {
                    bgcolor: selected ? 'action.selected' : 'action.hover',
                },
            }}
        >
            <RowDragHandle handleProps={handleProps} lifted={lifted}/>

            <Box sx={{display: 'inline-flex', flexShrink: 0, ...faded}}>
                {getSurveyQuestionTypeIcon(question.type, 18)}
            </Box>

            <Typography
                noWrap
                sx={{flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, ...faded}}
                title={row.title}
            >
                {name}
            </Typography>

            {/* Outside the title: that Typography is noWrap, so markers inside it truncate away. */}
            <SurveyQuestionMarkers question={question} size={16} gap={0}/>

            {/* Publishing leaves a switched-off question out, so its missing text holds nothing up. */}
            {!off && <MissingLanguagesMarker languages={row.missingLanguages}/>}

            {!!row.visibilitySummaries.length && (
                <Tooltip
                    title={
                        <Stack>
                            <Typography variant={'body2'}>{t('Shown only if:')}</Typography>
                            {row.visibilitySummaries.map((summary, index) => (
                                <Typography key={index} variant={'body2'}>{summary}</Typography>
                            ))}
                        </Stack>
                    }
                >
                    {row.visibilitySummaries.length > 1 ? (
                        <Badge
                            badgeContent={row.visibilitySummaries.length}
                            color={'info'}
                            sx={{
                                flexShrink: 0,
                                '& .MuiBadge-badge': {fontSize: 9, height: 13, minWidth: 13},
                            }}
                        >
                            {visibilityIcon}
                        </Badge>
                    ) : visibilityIcon}
                </Tooltip>
            )}

            <RowMenuButton name={name} onOpenMenu={onOpenMenu}/>
        </ListItem>
    );
}

interface Props {
    row: QuestionRowData
    selected: boolean
    onSelect: () => void
    onOpenMenu: (anchor: HTMLElement) => void
}

function SurveyQuestionRow({row, selected, onSelect, onOpenMenu}: Props) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: row.question.id!,
    });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                mb: 0.5,
                transform: CSS.Transform.toString(transform),
                transition,
                borderRadius: 1,
                ...(isDragging && {bgcolor: 'action.hover'}),
            }}
        >
            <Box sx={{...(isDragging && {visibility: 'hidden'})}}>
                <SurveyQuestionRowBase
                    row={row}
                    selected={selected}
                    handleProps={{...attributes, ...listeners}}
                    onSelect={onSelect}
                    onOpenMenu={onOpenMenu}
                />
            </Box>
        </Box>
    );
}

export default SurveyQuestionRow;
